/**
 * A member's profile, scoped to one pool (ISSUE-194, web half).
 *
 * This is not the fan's account profile. It carries what the member has done
 * IN THIS POOL and, for a Commissioner or an Admin, the way into managing
 * them. Platform spec followed: `.agents/docs/design/platforms/web.md` (R8).
 *
 * FIVE THINGS ARE DELIBERATE AND EASY TO "FIX" WRONGLY:
 *
 *  1. **There is no activity feed.** R3 and D3 were struck by the owner on
 *     2026-09-02 (Q-194-A, answer A), and pool-api's `/audit/list` is not
 *     it. A member's picks are reachable, though: each contest row opens the
 *     same picks sheet a standings row opens (ISSUE-1318), read from
 *     `/results/get-by-member`, which only ever returns locked picks.
 *
 *  2. **The awards block is the shared `MedalsBlock`, not a second one.**
 *     `features/awards/` already owns the two-number shape, the palette and
 *     the scope switch, and account home and the fan profile both mount it.
 *     A second web medals block would be a second place for those to drift.
 *     It also owns its own `scope` state, which is why this pane holds none.
 *
 *  3. **Management is one control that opens the EXISTING actions dialog.**
 *     Remove, promote, demote, suspend and hand over all live in
 *     `MemberActionsDialog`, wired by ISSUE-817 and computed from
 *     `buildMemberOverflowMenu`, the app's one client-side permission table.
 *     Re-listing them here would be a second copy of that table on a second
 *     surface, and two copies drift. Mobile makes the same call.
 *
 *  4. **A contest row opens that member's picks, it does not navigate.**
 *     The sheet rides `?sheet=member-contest-rounds&contestId=`, so browser
 *     back closes it and a refresh reopens it.
 *
 *  5. **Nothing here knows what an archived award type is** (SUR-R92, Q262).
 *     The read model never carries a type's state and this file never asks
 *     for one, so a grant of an archived type draws with the same name, the
 *     same palette color and the same chip in the same position as any other.
 */
import { useAwardGetForMember } from "@sportsmet/contest-sdk/private";
import { useMemberGet } from "@sportsmet/pool-sdk/private";
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import { Button, Skeleton } from "@sportsmet/ui";
import { Link, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useOverlay } from "../../routeState/useOverlay";
import { MedalsBlock } from "../awards/MedalsBlock";
import { FanMessageAction } from "../fanProfile/FanMessageAction";
import { VIEW_PROFILE_ACTION_LABEL } from "../fanProfile/fanProfileCopy";
import {
  AWARDS_SECTION_TITLE,
  AWARDS_UNAVAILABLE_LINE,
  BACK_ACTION_LABEL,
  CONTESTS_EMPTY_LINE,
  CONTESTS_SECTION_TITLE,
  MANAGE_ACTION_LABEL,
  MEMBER_MISSING_BODY,
  MEMBER_MISSING_TITLE,
  MEMBER_NO_IDS_LINE,
  PLAYING_NOW_LABEL,
  SCOPE_ALL_TIME_LABEL,
  SCOPE_TABS_LABEL,
  SCOPE_THIS_SEASON_LABEL,
} from "./memberDetailCopy";
import { buildMemberDetailModel } from "./memberDetailModel";
import { MemberRoundsSheet } from "../poolStandings/MemberRoundsSheet";

export const MEMBER_CONTEST_ROUNDS_SHEET_NAME = "member-contest-rounds";
export const CONTESTS_OPEN_HINT = "Tap a contest to see their picks.";

export type typePoolMemberDetailPaneProps = {
  poolId: string;
  memberId: string;
  onGoBack: () => void;
  onManageMember: (memberId: string) => void;
};

export const PoolMemberDetailPane = (
  props: typePoolMemberDetailPaneProps,
): ReactNode => {
  const hasIds = props.poolId !== "" && props.memberId !== "";

  const member = useMemberGet(
    { poolId: props.poolId, targetUserId: props.memberId },
    { enabled: hasIds },
  );
  const awards = useAwardGetForMember(
    { poolId: props.poolId, targetUserId: props.memberId },
    { enabled: hasIds },
  );
  const viewer = useProfileGet({}, { enabled: hasIds });
  const overlay = useOverlay();
  const search = useSearch({ strict: false }) as { contestId?: string };
  const sheetContestId =
    overlay.isOpen("sheet", MEMBER_CONTEST_ROUNDS_SHEET_NAME) &&
    typeof search.contestId === "string" &&
    search.contestId !== ""
      ? search.contestId
      : null;
  const viewerAccountId = viewer.data?.data?.profile?.accountId ?? "";

  const model = buildMemberDetailModel({
    poolId: props.poolId,
    isLoading: hasIds && (member.isLoading || viewer.isLoading),
    areAwardsLoading: awards.isLoading,
    member: member.data?.data ?? null,
    awards: awards.data?.data ?? null,
    haveAwardsFailed: awards.isError || Boolean(awards.data?.error),
    viewerUserId: viewerAccountId,
    targetUserId: props.memberId,
  });

  const backButton = (
    <Button
      variant="ghost"
      onClick={props.onGoBack}
      data-testid="member-detail-back"
    >
      {BACK_ACTION_LABEL}
    </Button>
  );

  if (model.phase === "noIds") {
    return (
      <div className="flex flex-col gap-4" data-testid="member-detail">
        <p
          className="text-body text-ink-secondary"
          data-testid="member-detail-no-ids"
        >
          {MEMBER_NO_IDS_LINE}
        </p>
        {backButton}
      </div>
    );
  }

  // R7. The skeleton is this layout's shape: the avatar plate and its two
  // lines, then the awards card, then the actions. Never a full-screen
  // spinner over a section (`.agents/rules/scoped-loading-state.mdc`).
  if (model.phase === "loading") {
    return (
      <div className="flex flex-col gap-4" data-testid="member-detail-loading">
        <Skeleton shape="circle" label="Loading member" />
        <Skeleton shape="text" />
        <Skeleton shape="text" />
        <Skeleton shape="card" />
        <Skeleton shape="control" />
      </div>
    );
  }

  if (model.phase === "missing") {
    return (
      <div className="flex flex-col gap-4" data-testid="member-detail">
        <h2 className="text-h2 text-ink" data-testid="member-detail-missing">
          {MEMBER_MISSING_TITLE}
        </h2>
        <p className="text-body text-ink-secondary">{MEMBER_MISSING_BODY}</p>
        {backButton}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-24" data-testid="member-detail">
      {backButton}

      <header
        className="flex flex-col items-center gap-1"
        data-testid="member-detail-header"
      >
        {model.avatarUrl === null ? (
          <span
            aria-hidden="true"
            className="grid size-16 shrink-0 place-items-center rounded-full bg-ink-secondary text-h2 font-bold text-ink-inverse"
          >
            {model.initials}
          </span>
        ) : (
          <img
            src={model.avatarUrl}
            alt=""
            className="size-16 shrink-0 rounded-full object-cover"
          />
        )}
        <h2 className="text-h2 text-ink" data-testid="member-detail-name">
          {model.displayName}
        </h2>
        {model.usernameLabel === null ? null : (
          <p
            className="text-caption text-ink-secondary"
            data-testid="member-detail-username"
          >
            {model.usernameLabel}
          </p>
        )}
        {model.roleLabel === null ? null : (
          <p
            className="text-caption text-ink-secondary"
            data-testid="member-detail-role"
          >
            {model.roleLabel}
          </p>
        )}
        {model.isPlayingNow ? (
          <p
            className="text-caption text-success"
            data-testid="member-detail-playing-now"
          >
            {PLAYING_NOW_LABEL}
          </p>
        ) : null}
      </header>

      <MedalsBlock
        isLoading={model.areAwardsLoading}
        hasFailed={model.haveAwardsFailed}
        thisSeason={model.thisSeason}
        allTime={model.allTime}
        chips={model.chips}
        title={AWARDS_SECTION_TITLE}
        thisSeasonLabel={SCOPE_THIS_SEASON_LABEL}
        allTimeLabel={SCOPE_ALL_TIME_LABEL}
        tabsLabel={SCOPE_TABS_LABEL}
        unavailableMessage={AWARDS_UNAVAILABLE_LINE}
        testId="member-detail-awards"
      />

      <section
        className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-4"
        data-testid="member-detail-contests"
      >
        <h2 className="text-h2 text-ink">{CONTESTS_SECTION_TITLE}</h2>
        {model.contests.length === 0 ? (
          <p
            className="text-body text-ink-secondary"
            data-testid="member-detail-contests-empty"
          >
            {CONTESTS_EMPTY_LINE}
          </p>
        ) : (
          <>
            <p className="text-caption text-ink-secondary">
              {CONTESTS_OPEN_HINT}
            </p>
            <ul className="flex flex-col">
              {model.contests.map((contest) => (
                <li
                  key={contest.contestId}
                  data-testid={`member-detail-contest-${contest.contestId}`}
                >
                  <button
                    type="button"
                    aria-haspopup="dialog"
                    className="flex min-h-touch-target w-full items-center justify-between gap-3 rounded-md text-left text-body text-ink transition-colors duration-200 ease-standard hover:bg-surface-sunken focus-visible:outline-2 focus-visible:outline-focus-ring motion-reduce:transition-none"
                    data-testid={`member-detail-contest-open-${contest.contestId}`}
                    onClick={() => {
                      overlay.open({
                        kind: "sheet",
                        name: MEMBER_CONTEST_ROUNDS_SHEET_NAME,
                        params: { contestId: contest.contestId },
                      });
                    }}
                  >
                    <span className="min-w-0 break-words">
                      {contest.contestName}
                    </span>
                    <span aria-hidden="true" className="text-ink-tertiary">
                      {"\u203A"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <div className="flex flex-col gap-2" data-testid="member-detail-actions">
        {viewerAccountId !== "" && !model.isSelf ? (
          <Button variant="secondary" asChild>
            <Link
              to="/fan"
              search={{ accountId: props.memberId }}
              data-testid="member-detail-view-profile"
            >
              {VIEW_PROFILE_ACTION_LABEL}
            </Link>
          </Button>
        ) : null}
        {model.showMessage ? (
          <div data-testid="member-detail-message">
            <FanMessageAction accountId={props.memberId} />
          </div>
        ) : null}
        {model.showManageControls ? (
          <Button
            variant="ghost"
            onClick={() => {
              props.onManageMember(props.memberId);
            }}
            data-testid="member-detail-manage"
          >
            {MANAGE_ACTION_LABEL}
          </Button>
        ) : null}
      </div>
      {sheetContestId === null ? null : (
        <MemberRoundsSheet
          open
          onOpenChange={(open) => {
            if (!open) {
              overlay.close({ kind: "sheet", clearParams: ["contestId"] });
            }
          }}
          poolId={props.poolId}
          contestId={sheetContestId}
          targetUserId={props.memberId}
          displayName={model.displayName}
        />
      )}
    </div>
  );
};
